import { authServices } from '@/shared/services/auth.services'
import type { AuthTokens } from '@/shared/types'
import { useAuthStore } from '@/store'
import type { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios'
import axios from 'axios'
import { ApiErrorResponse } from '../types'
import { apiErrorHandler } from './api.utils'
import { getSafeRedirectPath } from './authSession'

export type ApiTokenType = 'accessToken'

export type ManagedRequestConfig = InternalAxiosRequestConfig & {
  authTokenType?: ApiTokenType | false
  _managedAuth?: boolean
  _managedTokenType?: ApiTokenType
  _skipToast?: boolean
  _retry?: boolean
}

const SESSION_EXPIRED_ERROR = {
  error: 'Session expired. Please log in again.',
  statusCode: 401,
} as const

const FORBIDDEN_ERROR = {
  error: 'Access denied. Please log in again.',
  statusCode: 403,
} as const

const AUTH_PAGE_PATHS = ['/login', '/signup', '/auth/google', '/auth/callback', '/google/callback']

let isRefreshing = false
let refreshPromise: Promise<AuthTokens | null> | null = null
let isRedirectingToLogin = false

function redirectToLogin() {
  if (typeof window === 'undefined' || isRedirectingToLogin) {
    return
  }

  const { pathname, search } = window.location
  if (AUTH_PAGE_PATHS.some((page) => pathname === page)) {
    return
  }

  isRedirectingToLogin = true

  const currentPath = getSafeRedirectPath(`${pathname}${search}`)
  const loginUrl =
    currentPath !== '/'
      ? `/login?redirect=${encodeURIComponent(currentPath)}`
      : '/login'

  window.location.assign(loginUrl)
}

export function createAxiosInstance(baseURL?: string) {
  return axios.create({
    baseURL,
    timeout: 15000,
    headers: {
      Accept: 'application/json',
    },
  })
}

export function isAbsoluteUrl(url?: string) {
  return typeof url === 'string' && /^https?:\/\//.test(url)
}

export function getTokenFromStore(_tokenType: ApiTokenType): string | null {
  const state = useAuthStore.getState()
  return state.accessToken ?? authServices.getTokens()?.accessToken ?? null
}

export async function refreshManagedTokens(logoutOnFailure = false) {
  const hadRefreshToken = !!(authServices.getTokens()?.refreshToken)

  if (isRefreshing && refreshPromise) {
    const refreshedTokens = await refreshPromise
    if (!refreshedTokens && logoutOnFailure && hadRefreshToken) {
      await authServices.handleRefreshFailure()
    }
    return refreshedTokens
  }

  isRefreshing = true
  refreshPromise = (async () => {
    try {
      return await authServices.refreshTokens()
    } catch (error) {
      console.error('Token refresh failed:', error)
      return null
    } finally {
      isRefreshing = false
      refreshPromise = null
    }
  })()

  const refreshedTokens = await refreshPromise
  if (!refreshedTokens && logoutOnFailure && hadRefreshToken) {
    await authServices.handleRefreshFailure()
  }

  return refreshedTokens
}

export async function ensureValidToken(tokenType: ApiTokenType): Promise<string | null> {
  const currentToken = getTokenFromStore(tokenType)
  if (!currentToken) {
    return null
  }

  if (!authServices.isTokenExpired(60000)) {
    return currentToken
  }

  const refreshedTokens = await refreshManagedTokens(true)
  return refreshedTokens?.accessToken ?? getTokenFromStore(tokenType)
}

export function attachBaseUrl(
  config: ManagedRequestConfig,
  resolveBaseUrl: () => string,
) {
  if (config.baseURL || isAbsoluteUrl(config.url)) {
    return
  }

  config.baseURL = resolveBaseUrl()
}

export function extractSkipToastHeader(config: ManagedRequestConfig) {
  const headers = axios.AxiosHeaders.from(config.headers)
  const skipToastValue = headers.get('skipToast')
  const authTokenTypeValue = headers.get('authTokenType')

  config._skipToast =
    skipToastValue === true || skipToastValue === 'true' || skipToastValue === '1'

  if (config.authTokenType === undefined) {
    if (authTokenTypeValue === 'accessToken') {
      config.authTokenType = 'accessToken'
    }

    if (
      authTokenTypeValue === false ||
      authTokenTypeValue === 'false' ||
      authTokenTypeValue === '0'
    ) {
      config.authTokenType = false
    }
  }

  headers.delete('skipToast')
  headers.delete('authTokenType')
  config.headers = headers
}

export async function attachAuthHeader(config: ManagedRequestConfig) {
  const headers = axios.AxiosHeaders.from(config.headers)
  if (headers.has('Authorization')) {
    config.headers = headers
    return config
  }

  const requestedTokenType =
    config.authTokenType === undefined ? 'accessToken' : config.authTokenType
  if (requestedTokenType === false) {
    config.headers = headers
    return config
  }

  const token = await ensureValidToken(requestedTokenType)
  if (!token) {
    config.headers = headers
    return config
  }

  headers.set('Authorization', `Bearer ${token}`)
  config.headers = headers
  config._managedAuth = true
  config._managedTokenType = requestedTokenType
  return config
}

export function createManagedRequestInterceptor(
  resolveBaseUrl: () => string,
  tokenType: ApiTokenType = 'accessToken',
) {
  return async (config: InternalAxiosRequestConfig) => {
    const managedConfig = config as ManagedRequestConfig
    attachBaseUrl(managedConfig, resolveBaseUrl)
    extractSkipToastHeader(managedConfig)
    if (managedConfig.authTokenType === undefined) {
      managedConfig.authTokenType = tokenType
    }

    return attachAuthHeader(managedConfig)
  }
}

export function createPublicRequestInterceptor(resolveBaseUrl: () => string) {
  return async (config: InternalAxiosRequestConfig) => {
    const managedConfig = config as ManagedRequestConfig
    attachBaseUrl(managedConfig, resolveBaseUrl)
    extractSkipToastHeader(managedConfig)
    managedConfig.authTokenType = false
    return managedConfig
  }
}

export function createAuthResponseInterceptor(instance: AxiosInstance) {
  return async (error: AxiosError<ApiErrorResponse>) => {
    const originalRequest = error.config as ManagedRequestConfig | undefined

    if (error.response?.status === 403) {
      await authServices.handleRefreshFailure()
      redirectToLogin()
      return Promise.reject(FORBIDDEN_ERROR)
    }

    if (
      error.response?.status === 401 &&
      originalRequest &&
      originalRequest._managedAuth === true &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true

      try {
        const refreshedTokens = await refreshManagedTokens(true)
        const tokenType = originalRequest._managedTokenType ?? 'accessToken'
        const retryToken = refreshedTokens?.[tokenType] ?? getTokenFromStore(tokenType)

        if (retryToken) {
          const headers = axios.AxiosHeaders.from(originalRequest.headers)
          headers.set('Authorization', `Bearer ${retryToken}`)
          originalRequest.headers = headers
          return instance(originalRequest)
        }
      } catch (refreshError) {
        console.error('Token refresh failed on 401:', refreshError)
      }

      return Promise.reject(SESSION_EXPIRED_ERROR)
    }

    return Promise.reject(apiErrorHandler(error))
  }
}
